import React, { useState, useEffect } from 'react';
import ExportCsvButton from '../components/ExportCsvButton';
import { Helmet } from 'react-helmet';
import { useDomain } from '../contexts/DomainContext';
import { useAuth } from '../contexts/AuthContext';
import {
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  LinkIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { runSingleScan, getGeoEngines, getGeoState, saveGeoState } from '../services/geoService';

const ENGINE_COLORS = {
  google_ai: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', label: 'Google AI Overview' },
  perplexity: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'Perplexity' },
  chatgpt: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'ChatGPT' },
  claude: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: 'Claude' },
  bing: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', label: 'Bing' },
  you: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', label: 'You.com' },
  copilot: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', label: 'Copilot' }
};

// Breadcrumb
const GEOBreadcrumb = ({ items }) => (
  <nav aria-label="GEO breadcrumb" className="mb-4">
    <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
      {items.map((item, i) => (
        <li key={i} className="flex items-center" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
          {i > 0 && <span className="mx-2 text-gray-400">/</span>}
          {item.href ? (
            <a href={item.href} className="text-indigo-600 hover:text-indigo-800" itemProp="item">
              <span itemProp="name">{item.label}</span>
            </a>
          ) : (
            <span className="text-gray-600 font-medium" itemProp="name">{item.label}</span>
          )}
          <meta itemProp="position" content={String(i + 1)} />
        </li>
      ))}
    </ol>
  </nav>
);

export default function GEOAIPrompts() {
  const { selectedDomain } = useDomain();
  const { user } = useAuth();
  const [promptText, setPromptText] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  // Restore the last analysis for this domain.
  useEffect(() => {
    const d = (selectedDomain || '').trim();
    if (!d) return;
    let cancelled = false;
    (async () => {
      try {
        const saved = await getGeoState(d, 'ai-prompts');
        if (cancelled || !saved) return;
        if (saved.promptText) setPromptText(saved.promptText);
        if (Array.isArray(saved.selectedEngines) && saved.selectedEngines.length) setSelectedEngines(saved.selectedEngines);
        if (saved.scanResult) setScanResult(saved.scanResult);
      } catch (e) { console.error('Failed to load saved prompt analysis:', e); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);
  const [scanError, setScanError] = useState(null);
  const [expandedEngine, setExpandedEngine] = useState(null);
  const [selectedEngines, setSelectedEngines] = useState(['google_ai', 'perplexity', 'chatgpt']);
  const [engines, setEngines] = useState([]);

  useEffect(() => {
    const loadEngines = async () => {
      try {
        const data = await getGeoEngines();
        if (data && data.engines) setEngines(data.engines);
      } catch (e) { /* */ }
    };
    loadEngines();
  }, []);

  const handleScan = async () => {
    if (!promptText.trim()) {
      setScanError('Please enter a prompt to analyze.');
      return;
    }
    const domain = selectedDomain || 'serpfinity.ai';
    setScanning(true);
    setScanError(null);
    setScanResult(null);

    try {
      const result = await runSingleScan(promptText.trim(), domain, selectedEngines);
      if (result && result.data) {
        const flat = { ...result.data, ...(result.data.summary || {}) };
        setScanResult(flat);
        saveGeoState(domain, 'ai-prompts', { promptText, selectedEngines, scanResult: flat, savedAt: new Date().toISOString() }).catch(() => {});
      } else {
        setScanError('No data returned from scan.');
      }
    } catch (err) {
      console.error('Prompt scan failed:', err);
      setScanError(err.message || 'Scan failed.');
    } finally {
      setScanning(false);
    }
  };

  const toggleEngine = (id) => {
    setSelectedEngines(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'GEO', href: '/geo' },
    { label: 'AI Prompts' }
  ];

  return (
    <>
      <Helmet>
        <title>AI Prompt Analysis | GEO | SERPfinity™</title>
        <meta name="description" content="Analyze how AI search engines respond to specific prompts. View full AI responses, citations, and brand mentions from ChatGPT, Perplexity, Google AI Overview." />
        <link rel="canonical" href="https://thedigitalhall.club/geo/ai-prompts" />
        <meta property="og:title" content="AI Prompt Analysis | GEO | SERPfinity™" />
        <meta property="og:url" content="https://thedigitalhall.club/geo/ai-prompts" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "AI Prompt Analysis",
            "description": "Analyze AI search engine responses with SERPfinity™.",
            "url": "https://thedigitalhall.club/geo/ai-prompts"
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <GEOBreadcrumb items={breadcrumbItems} />

          <header className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">AI Prompt Analysis</h1>
          <ExportCsvButton data={scanResult} filename="geo-ai-prompts" />
            <p className="mt-1 text-gray-600">Enter a prompt to see how each AI engine responds and whether your brand is mentioned or cited.</p>
          </header>

          {/* Scan Panel */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" aria-label="Prompt scan input">
            <div className="mb-4">
              <label htmlFor="prompt-input" className="block text-sm font-medium text-gray-700 mb-1">Enter your prompt</label>
              <div className="flex gap-3">
                <input
                  id="prompt-input"
                  type="text"
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                  placeholder="e.g. what are the best AI SEO tools?"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleScan}
                  disabled={scanning}
                  className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white font-medium ${scanning ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  aria-busy={scanning}
                >
                  {scanning ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4" />
                      Analyze
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {engines.filter(e => e.status === 'active').map(eng => (
                <button
                  key={eng.id}
                  onClick={() => toggleEngine(eng.id)}
                  className={`px-3 py-1 rounded-full text-xs border ${selectedEngines.includes(eng.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                  aria-pressed={selectedEngines.includes(eng.id)}
                >
                  {eng.name}
                </button>
              ))}
            </div>
          </section>

          {/* Error */}
          {scanError && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" role="alert">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{scanError}</p>
            </div>
          )}

          {/* Empty state */}
          {!scanResult && !scanning && !scanError && (
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <SparklesIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Analyze Any Prompt</h2>
              <p className="text-gray-500 max-w-md mx-auto">Type a prompt above and click Analyze to see how ChatGPT, Perplexity, and Google AI respond.</p>
            </section>
          )}

          {/* Results */}
          {scanResult && (
            <section aria-label="Scan results">
              {/* Summary bar */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Results for: &quot;{scanResult.prompt || promptText}&quot;</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{scanResult.totalEngines || 0} engines scanned &middot; {scanResult.mentions || 0} mentions &middot; Mention rate: {scanResult.mentionRate || 0}%</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-600">{scanResult.citationRate || 0}%</div>
                    <div className="text-xs text-gray-500">Citation Rate</div>
                  </div>
                </div>
              </div>

              {/* Per-engine results */}
              <div className="space-y-4">
                {(scanResult.engines || []).map((eng, idx) => {
                  const colors = ENGINE_COLORS[eng.engine] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', label: eng.engine };
                  const isExpanded = expandedEngine === idx;
                  return (
                    <div key={idx} className={`rounded-xl border ${colors.border} overflow-hidden`}>
                      {/* Engine header */}
                      <button
                        onClick={() => setExpandedEngine(isExpanded ? null : idx)}
                        className={`w-full flex items-center justify-between p-4 ${colors.bg} hover:opacity-90 transition-opacity`}
                        aria-expanded={isExpanded}
                      >
                        <div className="flex items-center gap-3">
                          <GlobeAltIcon className={`h-5 w-5 ${colors.text}`} />
                          <span className={`font-semibold ${colors.text}`}>{colors.label}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${eng.mentioned ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                            {eng.mentioned ? 'Mentioned' : 'Not mentioned'}
                          </span>
                          {eng.cited && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Cited</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {eng.rank > 0 && <span className="text-sm text-gray-500">Rank: {eng.rank}</span>}
                          {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                        </div>
                      </button>

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="p-4 border-t border-gray-100">
                          {/* Response snippet */}
                          {eng.snippet && (
                            <div className="mb-4">
                              <h3 className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <DocumentTextIcon className="h-4 w-4" /> AI Response
                              </h3>
                              <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 leading-relaxed">{eng.snippet}</p>
                            </div>
                          )}

                          {/* Sources / Citations */}
                          {eng.sources && eng.sources.length > 0 && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                <LinkIcon className="h-4 w-4" /> Sources ({eng.sources.length})
                              </h3>
                              <ul className="space-y-1">
                                {eng.sources.map((src, sIdx) => (
                                  <li key={sIdx} className="text-sm text-indigo-600 hover:text-indigo-800 truncate">
                                    <a href={typeof src === 'string' ? src : src.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1">
                                      <GlobeAltIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                      {typeof src === 'string' ? src : (src.url || src.name || 'Source')}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Error */}
                          {eng.error && (
                            <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
                              <span className="font-medium">Error:</span> {eng.error}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
